import React from 'react';
import PropTypes from 'prop-types';
import { SlateDialog } from 'components/Shared/SlateDialog';

// Translate with a fallback when no key is registered (t returns the key).
function tf(t, key, fallback) {
  if (!t) return fallback;
  var translated = t(key);
  return translated === key ? fallback : translated;
}

var FACTOR_META = {
  passRate: {
    nameKey: 'inspect:perfPassRate',
    nameFallback: 'Pass Rate',
    defKey: 'inspect:perfInfoDefPassRate',
    defFallback: 'Graded inspections that passed or were corrected',
  },
  completion: {
    nameKey: 'inspect:perfFactorCompletion',
    nameFallback: 'Completion',
    defKey: 'inspect:perfInfoDefCompletion',
    defFallback: 'Inspections finished and graded',
  },
  onSchedule: {
    nameKey: 'inspect:perfFactorOnSchedule',
    nameFallback: 'On Schedule',
    defKey: 'inspect:perfInfoDefOnSchedule',
    defFallback: 'Scheduled inspections not overdue',
  },
};

var GRADE_FALLBACKS = { good: 'Good', fair: 'Fair', poor: 'Poor' };

// Explains the weighted health score with the account's live numbers: one row
// per factor (effective weight, current value, contribution), the neglect
// penalty row, and the composite-minus-penalty total. Dropped factors render
// dimmed with "No data"; the neglect row shows an em dash until the endpoint
// delivers the count. The view prop is the computeHealthView result the card
// itself rendered, so the modal always matches the gauge.
function InspectHealthInfoDialog(props) {
  var view = props.view;
  var t = props.t;
  if (!view) return null;

  var factorRows = view.factors.map(function (f, index) {
    var meta = FACTOR_META[f.key];
    var rowClass = 'perf-info-row' + (f.present ? '' : ' dimmed');
    // The meter shares the gauge's grade bands so a low factor reads in the
    // same color language as the score it drags down.
    var band = null;
    if (f.present) {
      band = f.value >= 75 ? 'good' : f.value >= 50 ? 'ok' : 'bad';
    }
    return (
      <div key={f.key} className={rowClass}>
        <div className="perf-info-row-head">
          <span className="perf-info-name">
            {tf(t, meta.nameKey, meta.nameFallback)}
          </span>
          <span className="perf-info-weight">
            {f.present
              ? tf(t, 'inspect:perfInfoWeight', '{n}% of score').replace(
                  '{n}',
                  Math.round(f.effectiveWeight * 100),
                )
              : '—'}
          </span>
        </div>
        <div className="perf-info-vals">
          <span className="perf-info-value">
            {f.present
              ? String(Math.round(f.value))
              : tf(t, 'inspect:perfInfoNoData', 'No data')}
          </span>
          {f.present && (
            <div className="perf-info-meter">
              <div
                className={'perf-info-meter-fill ' + band}
                style={{
                  width: Math.round(f.value) + '%',
                  '--d': index * 60 + 'ms',
                }}
              />
            </div>
          )}
          <span className="perf-info-contrib">
            {f.present
              ? tf(t, 'inspect:perfInfoPts', '{n} pts').replace(
                  '{n}',
                  '+' + (f.effectiveWeight * f.value).toFixed(1),
                )
              : ''}
          </span>
        </div>
        <div className="perf-info-def">
          {tf(t, meta.defKey, meta.defFallback)}
        </div>
      </div>
    );
  });

  var neglect = view.neglect;
  var neglectValue = '—';
  if (neglect.available) {
    neglectValue = neglect.penalty > 0 ? '-' + neglect.penalty : '0';
  }
  var neglectRow = (
    <div
      className={'perf-info-row neglect' + (neglect.available ? '' : ' dimmed')}
    >
      <div className="perf-info-row-head">
        <span className="perf-info-name">
          {tf(t, 'inspect:perfInfoNeglectTitle', 'Neglect Penalty')}
        </span>
        <span className="perf-info-weight">
          {neglect.available
            ? tf(t, 'inspect:perfInfoNeglectCount', '{n} neglected').replace(
                '{n}',
                neglect.count,
              )
            : tf(t, 'inspect:perfInfoNeglectPending', 'Not reported yet')}
        </span>
      </div>
      <div className="perf-info-vals">
        <span className="perf-info-value">{neglectValue}</span>
        <span className="perf-info-contrib">
          {tf(t, 'inspect:perfInfoNeglectRule', '-3 pts each, max -15')}
        </span>
      </div>
    </div>
  );

  var gradeLabel = tf(
    t,
    'inspect:perfGrade_' + view.gradeKey,
    GRADE_FALLBACKS[view.gradeKey],
  );

  return (
    <SlateDialog
      open={props.open}
      onClose={props.onClose}
      title={tf(t, 'inspect:perfInfoTitle', 'How Health Score Is Calculated')}
      maxWidth="sm"
    >
      <div className="perf-info-content">
        {factorRows}
        {neglectRow}
        <div className="perf-info-total">
          <span className="perf-info-total-eq">
            {tf(t, 'inspect:perfInfoTotal', '{c} - {p} penalty')
              .replace('{c}', view.composite)
              .replace('{p}', view.neglect.penalty)}
          </span>
          <span
            className="perf-info-total-score"
            style={{ color: view.colorVar }}
          >
            {view.score + ' ' + gradeLabel}
          </span>
        </div>
        <div className="perf-info-footnote">
          {tf(
            t,
            'inspect:perfInfoThresholds',
            'Good 75+ · Fair 50-74 · Poor below 50',
          )}
        </div>
      </div>
    </SlateDialog>
  );
}

InspectHealthInfoDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  view: PropTypes.object,
  t: PropTypes.func,
};

export default InspectHealthInfoDialog;
